/**
 * Sign-in token handoff for the React Native WebView.
 *
 * The mobile app loads webby-app with `?token=<firebase custom token>` so the
 * WebView lands already authenticated. Left in the URL, that credential ends up
 * in browser history, in the same-origin `Referer` header sent to webby-server
 * (and so in load balancer and access logs), and in every Datadog RUM
 * `view.url` / `view.referrer` for the session.
 *
 * `captureAuthTokenFromUrl` is called from the entrypoint before Datadog RUM
 * initializes: it moves the token into memory and rewrites the address bar, so
 * nothing downstream ever observes it.
 */

let capturedToken: string | null = null;

/**
 * Move `?token=` out of the URL and into memory. Safe to call when no token is
 * present. Must run before RUM initializes and before the app renders.
 */
export function captureAuthTokenFromUrl(): void {
    const url = new URL(window.location.href);
    const token = url.searchParams.get("token");
    if (!token) return;

    capturedToken = token;
    url.searchParams.delete("token");
    window.history.replaceState(window.history.state, "", url.toString());
}

/** The captured token, or null if the page was not opened with one. */
export function getAuthToken(): string | null {
    return capturedToken;
}

/** Drop the token once it has been exchanged for a Firebase session. */
export function clearAuthToken(): void {
    capturedToken = null;
}
